/**
 * Email validation helper functions
 */

/**
 * Validates if the provided string is a valid email address
 * @param email - The email string to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check if email matches the basic pattern
  if (!emailRegex.test(email)) {
    return false;
  }

  // Additional checks for common issues
  const [localPart, domain] = email.split('@');

  // Check local part length (1-64 characters)
  if (localPart.length < 1 || localPart.length > 64) {
    return false;
  }

  // Check domain length (1-253 characters)
  if (domain.length < 1 || domain.length > 253) {
    return false;
  }

  // Check if domain has at least one dot and valid TLD
  const tld = domain.split('.').pop();
  if (!domain.includes('.') || !tld || tld.length < 2) {
    return false;
  }

  return true;
};

/**
 * Gets a user-friendly error message for email validation
 * @param email - The email string that failed validation
 * @returns A descriptive error message
 */
export const getEmailErrorMessage = (email: string): string => {
  if (!email || email.trim() === '') {
    return 'Email address is required';
  }

  if (!email.includes('@')) {
    return 'Email address must contain @ symbol';
  }

  if (!email.includes('.')) {
    return 'Email address must contain a valid domain';
  }

  const [localPart, domain] = email.split('@');

  if (localPart.length === 0) {
    return 'Email address must have a local part before @';
  }

  if (domain.length === 0) {
    return 'Email address must have a domain after @';
  }

  if (!domain.includes('.')) {
    return 'Email domain must contain a valid TLD';
  }

  return 'Please enter a valid email address';
};
